import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import './FormMonivaeReferral.css';
import { useFormsSwitchedOff, PreviewFormNotice } from '../components/PreviewFormNotice';

const FormMonivaeReferral = () => {
  const [uploading, setUploading] = useState(false);
  const formsOff = useFormsSwitchedOff();
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [formData, setFormData] = useState({
    patientName: '',
    referringPractitioner: '',
    contactEmail: '',
    file: null
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      file: e.target.files[0]
    }));
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (formsOff) return;
    setUploading(true);
    setUploadError('');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('patient_name', formData.patientName);
      formDataToSend.append('referring_practitioner', formData.referringPractitioner);
      formDataToSend.append('contact_email', formData.contactEmail);
      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/monivae-referral/upload`, {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setUploadSuccess(true);
        // Reset form after 3 seconds
        setTimeout(() => {
          setUploadSuccess(false);
          setFormData({
            patientName: '',
            referringPractitioner: '',
            contactEmail: '',
            file: null
          });
        }, 3000);
      } else {
        setUploadError(data.detail || 'Upload failed. Please try again.');
      }
    } catch (error) {
      setUploadError('Network error. Please check your connection and try again.');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="form-monivae-referral-standalone">
      <div className="form-container">
        <div className="form-header">
          <div className="form-branding">
            <img src="/logo.png" alt="Clinical Minds Psychology" className="form-logo" />
          </div>
          <h1 className="form-title">Upload Referral - Monivae</h1>
          <p className="form-subtitle">Upload your patient's referral documentation securely</p>
        </div>

        <div className="form-content">
          {uploadSuccess ? (
            <div className="success-message">
              <CheckCircle size={64} className="success-icon" />
              <h3>Referral Uploaded Successfully!</h3>
              <p>Thank you. We'll contact the patient within 24-48 hours.</p>
            </div>
          ) : (
            <form onSubmit={handleFileUpload} className="referral-form">
              {formsOff && <PreviewFormNotice />}
              <div className="privacy-notice">
                <p>We take your privacy seriously. All data and files you submit are securely stored in Australia in compliance with The Privacy Act (1988) and the Australian Privacy Principles (APPs). You can find full details in our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
              </div>

              <div className="form-group">
                <label htmlFor="patientName">Patient Name *</label>
                <input
                  type="text"
                  id="patientName"
                  name="patientName"
                  value={formData.patientName}
                  onChange={handleInputChange}
                  required
                  disabled={uploading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="referringPractitioner">Referring Practitioner *</label>
                <input
                  type="text"
                  id="referringPractitioner"
                  name="referringPractitioner"
                  value={formData.referringPractitioner}
                  onChange={handleInputChange}
                  required
                  disabled={uploading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="contactEmail">Contact Email *</label>
                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={formData.contactEmail}
                  onChange={handleInputChange}
                  required
                  disabled={uploading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="file">Referral Document (Optional)</label>
                <input
                  type="file"
                  id="file"
                  name="file"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploading}
                  className="file-input"
                />
                <p className="form-help">
                  Accepted formats: PDF, Word, or images (max 10MB)
                </p>
              </div>

              {uploadError && (
                <div className="error-message">
                  <p>{uploadError}</p>
                </div>
              )}

              <div className="form-footer">
                <p className="consent-text">
                  By submitting this form, you confirm that you have read and agree to our <a href="https://www.clinicalminds.net/privacy-policy" target="_blank" rel="noopener noreferrer">privacy policy</a>.
                </p>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={uploading || formsOff}
                >
                  {uploading ? (
                    <>
                      <span className="spinner"></span>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Submit Referral
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormMonivaeReferral;